import { LazyLoader } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import {
  LessonEditStatus,
  LessonItemFragment,
  useLessonsForTimetableVersionQuery,
} from '../../../../types/planung-graphql-client-defs';
import { FormBlockHeader } from '../../../Form/FormBlockHeader';
import { ModalBottomButtons } from '../../../ModalBottomButtons/ModalBottomButtons';
import { LessonBasicsForm } from '../../Forms/Basics/LessonBasicsForm';
import { LessonClassesForm } from '../../Forms/Classes/LessonClassesForm';
import { TimetableDurationAndCounter } from '../../Forms/DurationAndCounter/TimetableDurationAndCounter';
import { LessonRoomSupplyForm } from '../../Forms/RoomSupply/LessonRoomSupplyForm';
import { LessonTeacherForm } from '../../Forms/Teachers/LessonTeacherForm';
import { schema } from '../../Forms/validation/schema';
import { VersionLessonFormType } from '../../types';
import { useTimetableLessons } from '../hooks/useTimetableLessons';

type LessonVersionFormProps = {
  closeForm: () => void;
  onLessonEdited: (response: { error?: unknown; data?: LessonItemFragment }) => void;
  onLessonCreated: (response: { error?: unknown; data?: LessonItemFragment }) => void;
  lessonUuid?: string | null;
  defaultInitialValues: Partial<VersionLessonFormType>;
  versionUuid?: string;
  lessonsData: {
    uuid: string;
    editStatus?: LessonEditStatus;
  }[];
};

export const TimetableVersionLessonForm = observer(
  ({
    closeForm,
    lessonUuid,
    defaultInitialValues,
    versionUuid,
    lessonsData,
    onLessonCreated,
    onLessonEdited,
  }: LessonVersionFormProps) => {
    const { t } = useTranslation();
    const schoolYear = useSelectedSchoolYear();

    const { createLesson, updateLesson } = useTimetableLessons({
      schoolYearUuid: schoolYear?.uuid ?? '',
      versionUuid: versionUuid,
    });

    const currentLessonsData = lessonsData.find((lesson) => {
      return lesson.uuid === lessonUuid;
    });
    const [{ data: queryLessonsData }] = useLessonsForTimetableVersionQuery({
      variables: { versionUuid: versionUuid ?? '' },
      pause: !versionUuid || versionUuid === '',
    });
    const lesson = queryLessonsData?.lessonsForTimetableVersion.find((lesson) => {
      return lesson.uuid === lessonUuid;
    });

    const initialValues: VersionLessonFormType = lesson
      ? {
          uuid: lesson.uuid,
          subject: {
            uuid: lesson.subject.uuid,
            name: lesson.subject.name,
          },
          lessonClasses: lesson.lessonClasses.map((node) => {
            return {
              uuid: node.uuid,
              groups: node.groups,
              class: {
                uuid: node.class.uuid,
                name: node.class.name,
                color: node.class.color,
                grade: node.class.grade,
              },
            };
          }),
          comment: lesson.comment,
          elective: lesson.elective,
          isClassTrip: lesson.isClassTrip,
          teachingLoadEnabled: lesson.teachingLoadEnabled,
          timetableEnabled: lesson.timetableEnabled,
          subTitle: lesson.subTitle,
          roomSupply: lesson.roomSupply.map((room) => ({
            uuid: room.uuid,
            description: room.descriptions ?? '',
          })),
          teachers: lesson.teachers.map((teacher) => {
            return {
              present: teacher.present,
              teachingLoad: teacher.teachingLoad,
              teachingLoadHours: teacher.teachingLoadHours,
              description: teacher.description,
              writesCertificate: teacher.writesCertificate,
              person: {
                uuid: teacher.uuid,
                fullName: teacher.fullName,
                displayNameShort: teacher.displayNameShort || teacher.fullName,
                color: teacher.color,
              },
            };
          }),
          durationCountArray: [
            {
              duration: 1,
              count: 1,
            },
          ],
        }
      : {
          ...defaultInitialValues,
          timetableEnabled: true,
          teachingLoadEnabled: true,
          durationCountArray: [
            {
              duration: 1,
              count: 1,
            },
          ],
        };

    const handleSubmit = async (values: VersionLessonFormType, formikHelpers: FormikHelpers<VersionLessonFormType>) => {
      if (!values.uuid) {
        const createResult = await createLesson(values);
        onLessonCreated({ error: createResult.error, data: createResult.data });
        formikHelpers.resetForm();
      } else {
        const updateResult = await updateLesson(values);
        onLessonEdited({ error: updateResult.error, data: updateResult.data });
        formikHelpers.resetForm();
      }
    };

    const disabled = currentLessonsData?.editStatus === LessonEditStatus.Blocked;

    return (
      <Suspense fallback={<LazyLoader transparent forceHeight='40vh' />}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
          {(formik) => (
            <Form>
              <div className='form-block'>
                <div className={'form-group'}>
                  <LessonBasicsForm status={currentLessonsData?.editStatus} timetableDisabled={true} />
                </div>
              </div>
              <div className='form-block'>
                <FormBlockHeader title={t('classes.title', { count: 2 })} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonClassesForm disabled={disabled} />
                </Suspense>
              </div>
              <div className='form-block'>
                <FormBlockHeader title={t('persons.title', { count: 1 })} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonTeacherForm
                    disabled={disabled}
                    defaults={{ teachingLoad: true, present: true, writesCertificate: true }}
                  />
                </Suspense>
              </div>
              {!formik.values.uuid && (
                <div className='form-block'>
                  <FormBlockHeader title={t('badgeCard.title', { count: 2 })} />
                  <Suspense fallback={<LazyLoader />}>
                    <TimetableDurationAndCounter />
                  </Suspense>
                </div>
              )}
              <div className='form-block'>
                <FormBlockHeader title={t('rooms.stock')} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonRoomSupplyForm key={formik.values.subject?.uuid} disabled={disabled} />
                </Suspense>
              </div>
              <ModalBottomButtons
                closeButton={{
                  text: disabled ? t('common.close') : t('common.cancel'),
                  callback: () => {
                    formik.resetForm();
                    closeForm();
                  },
                }}
                submitButton={{ disabled: disabled }}
                isLoading={formik.isSubmitting}
                errors={formik.errors}
              />
            </Form>
          )}
        </Formik>
      </Suspense>
    );
  },
);
